import axios from "axios";
import i18n from "../i18n";

const getBackendBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl && envUrl !== "http://localhost:8000") {
    return envUrl;
  }
  if (typeof window !== "undefined") {
    const isCapacitor = window.Capacitor || window.location.protocol === "capacitor:" || window.location.protocol === "file:";
    if (isCapacitor) {
      return "https://tantaapp.net";
    }
  }
  return "";
};

const BASE = getBackendBaseUrl();

// Single source of truth for building absolute URLs to backend-served assets
// (uploads, media). Pages must use this instead of REACT_APP_BACKEND_URL,
// which is localhost-only and breaks for visitors on other devices.
export const BACKEND_BASE = BASE;

// Auth is natively handled by Clerk via Bearer tokens.
const api = axios.create({
  baseURL: `${BASE}/api`,
});

// Attach the current i18n language as X-Lang so the backend can render emails
// and other server-rendered text in the same language the user is viewing.
// Also attach the Clerk Bearer token natively.
api.interceptors.request.use(async (config) => {
  const lang = (i18n.language || "en").split("-")[0].toLowerCase();
  config.headers = config.headers || {};
  config.headers["X-Lang"] = lang;

  const mobileToken = typeof window !== "undefined" ? localStorage.getItem("tanta_mobile_token") : null;
  const guestToken = typeof window !== "undefined" ? localStorage.getItem("tanta_guest_token") : null;
  if (mobileToken) {
    config.headers.Authorization = `Bearer ${mobileToken}`;
  } else if (guestToken) {
    config.headers.Authorization = `Bearer ${guestToken}`;
  } else if (window.Clerk) {
    try {
      let token = null;
      if (window.Clerk.session) {
        token = await window.Clerk.session.getToken();
      } else if (typeof window.Clerk.getToken === "function") {
        token = await window.Clerk.getToken();
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      console.warn("Failed to get Clerk token:", e);
    }
  }
  return config;
});

// 401 handler — when the session dies mid-flight (JWT expired, token_version
// bumped by logout/password reset), we redirect to /auth once and swallow the
// follow-up rejections so pages that only have `try/finally` (no catch) don't
// surface an "Uncaught 401" in the dev overlay while React is already
// unmounting the tree. `/auth/*` endpoints are exempt — Auth pages need to see
// their own 401s to render "wrong password" etc.
let sessionExpiredHandled = false;

/** Call this after a successful login to re-arm the expiry guard. */
export function resetSessionExpiredGuard() {
  sessionExpiredHandled = false;
}

api.interceptors.response.use(
  (r) => {
    // A successful authenticated response means we have a live session — reset
    // the guard so that a future expiry can trigger the redirect again.
    if (r?.config?.headers?.Authorization) {
      sessionExpiredHandled = false;
    }
    return r;
  },
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const isExemptEndpoint = url.startsWith("/auth/") || url.startsWith("/agent/") || url.startsWith("/tickets/");
    if (status === 401 && !isExemptEndpoint) {
      if (!sessionExpiredHandled && typeof window !== "undefined") {
        sessionExpiredHandled = true;
        try { window.dispatchEvent(new CustomEvent("tanta:session-expired")); } catch (_) { /* ignore */ }
      }
      // Return a resolved shape that pages awaiting `.data` won't crash on —
      // they're being unmounted anyway.
      return Promise.reject(Object.assign(err, { __silent401: true }));
    }
    return Promise.reject(err);
  },
);

// Global safety net: swallow any 401 rejection that a component forgot to
// catch. This prevents React's dev overlay from popping up during the brief
// window between session death and the redirect to /auth.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    if (event.reason && event.reason.__silent401) {
      event.preventDefault();
    }
  });
}

export default api;

export function fmtError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
